import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { authAPI, assetsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import useDocumentTitle from '../../hooks/useDocumentTitle';

const OTPVerificationPage: React.FC = () => {
  useDocumentTitle('Verify OTP');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, setAssets, setCurrentAsset } = useAuth();

  const state = location.state as { email?: string; type?: 'login' | 'signup' };
  const email = state?.email;
  const type = state?.type;

  useEffect(() => {
    if (!email || !type) {
      toast.error('Invalid verification request');
      navigate('/login');
      return;
    }

    // Start countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [email, type, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otp.trim() || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);
    try {
      if (type === 'login') {
        const response = await authAPI.verifyLoginOTP(email!, otp);
        if (response.data.status === 'success') {
          const { user, token } = response.data;
          login(user, token);
          toast.success('Login successful!');

          // Fetch user's assets after login
          try {
            const assetsResponse = await assetsAPI.list();
            if (assetsResponse.data.status === 'success' && assetsResponse.data.data) {
              // Assets are returned in data.assets array
              const userAssets = assetsResponse.data.data.assets || [];
              setAssets(userAssets);

              if (userAssets.length > 0) {
                // Set the first asset as current (or the last used one if available)
                setCurrentAsset(userAssets[0]);
                navigate('/analytics/summary');
              } else {
                // No assets, redirect to setup
                navigate('/setup/asset');
              }
            } else {
              navigate('/setup/asset');
            }
          } catch (assetError) {
            console.error('Failed to fetch assets:', assetError);
            navigate('/setup/asset');
          }
        } else {
          toast.error(response.data.message || 'Invalid verification code');
          setOtp('');
        }
      } else if (type === 'signup') {
        const response = await authAPI.verifySignupOTP(email!, otp);
        if (response.data.status === 'success') {
          toast.success('Email verified! Please complete registration.');
          navigate('/register', { state: { email, verified: true } });
        } else {
          toast.error(response.data.message || 'Invalid verification code');
          setOtp('');
        }
      }
    } catch (error: any) {
      console.error('OTP verification error:', error);
      toast.error(error.response?.data?.message || 'Invalid verification code');
      setOtp('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsLoading(true);
    try {
      if (type === 'login') {
        await authAPI.sendLoginOTP(email!);
      } else {
        await authAPI.sendSignupOTP(email!);
      }
      toast.success('New verification code sent');
      setCanResend(false);
      setCountdown(60);
      
      // Restart countdown
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      toast.error('Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '').substring(0, 6);
    setOtp(value);
  };

  if (!email || !type) {
    return null;
  }

  return (
    <div className="min-vh-100 d-flex align-items-center bg-light">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-4">
            <div className="card shadow">
              <div className="card-body p-4">
                {/* Header */}
                <div className="text-center mb-4">
                  <h1 className="h3 text-primary fw-bold">CloudAyn</h1>
                  <p className="text-muted">Enter verification code</p>
                </div>

                {/* Status */}
                <div className="text-center mb-4">
                  <div className="mx-auto d-flex align-items-center justify-content-center bg-success bg-opacity-10 rounded-circle" style={{ width: '60px', height: '60px' }}>
                    <i className="bi bi-check-circle text-success fs-4"></i>
                  </div>
                  <h5 className="mt-3 mb-1">Check your email</h5>
                  <p className="text-muted small">
                    We sent a verification code to<br />
                    <strong>{email}</strong>
                  </p>
                </div>

                {/* OTP Form */}
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="otp" className="form-label text-center w-100">
                      Verification Code
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg text-center"
                      id="otp"
                      value={otp}
                      onChange={handleOtpChange}
                      placeholder="000000"
                      maxLength={6}
                      style={{ letterSpacing: '0.5em', fontSize: '1.5rem' }}
                      autoFocus
                      disabled={isLoading}
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={isLoading || otp.length !== 6}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Verifying...
                      </>
                    ) : (
                      'Verify Code'
                    )}
                  </button>
                </form>

                {/* Resend */}
                <div className="text-center mt-3">
                  {canResend ? (
                    <button
                      onClick={handleResend}
                      className="btn btn-link text-primary p-0"
                      disabled={isLoading}
                    >
                      Resend code
                    </button>
                  ) : (
                    <span className="text-muted small">
                      Resend code in {countdown}s
                    </span>
                  )}
                </div>

                {/* Back Link */}
                <div className="text-center mt-3">
                  <Link 
                    to={type === 'login' ? '/login' : '/signup'} 
                    className="text-muted text-decoration-none small"
                  >
                    ← Back to {type === 'login' ? 'Login' : 'Signup'}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPVerificationPage;